import { useEffect, useState } from "react";

// Shared PWA state: the deferred install prompt (captured as early as possible) and a waiting service-worker update.
const state = { prompt: null, waiting: null };
const subs = new Set();
const emit = () => subs.forEach((f) => f({ ...state }));

window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); state.prompt = e; emit(); });
window.addEventListener("appinstalled", () => { state.prompt = null; emit(); });

export const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isIosSafari = () => {
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(ua);
};

export const registerServiceWorker = () => {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (e) => {
    if (e.data?.type === "am:navigate" && typeof e.data.url === "string" && new URL(e.data.url, window.location.href).origin === window.location.origin) window.location.assign(e.data.url);
  });
  window.addEventListener("load", async () => {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      const offer = (w) => { if (w && navigator.serviceWorker.controller) { state.waiting = w; emit(); } };
      offer(reg.waiting);
      reg.addEventListener("updatefound", () => {
        const w = reg.installing;
        w && w.addEventListener("statechange", () => w.state === "installed" && offer(w));
      });
      const check = () => reg.update().catch(() => {});
      setInterval(check, 30 * 60 * 1000);
      document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && check());
      let reloaded = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => { if (state.reloading && !reloaded) { reloaded = true; window.location.reload(); } });
    } catch { /* SW is an enhancement only */ }
  });
};

export const applyUpdate = () => {
  state.reloading = true;
  if (state.waiting) state.waiting.postMessage("SKIP_WAITING");
  else window.location.reload();
};

export const promptInstall = async () => {
  if (!state.prompt) return false;
  state.prompt.prompt();
  const { outcome } = await state.prompt.userChoice;
  state.prompt = null;
  emit();
  return outcome === "accepted";
};

export const usePwa = () => {
  const [s, setS] = useState({ ...state });
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    subs.add(setS);
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { subs.delete(setS); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  const standalone = isStandalone();
  return { canInstall: !!s.prompt && !standalone, updateReady: !!s.waiting, standalone, online };
};
